// M1 THROWAWAY (OPTI-6761): pre-flight snapshot for the Live Analytics chat
// system prompt. Issues a single batched call to the prototype
// `/analytics/api/v1/query` endpoint (workspace-services PR #12620) and formats
// the markdown blocks the prompt is seeded with: the top `strings`/`numerics`
// MAP keys for the flow, and the flow versions seen recently. Delete once the
// proper agent lifecycle lands.

import axios from "axios";

const METRICS_QUERY_PATH = "/analytics/api/v1/query";
const FLOW_ID_PATTERN = /^[0-9a-fA-F-]{36}$/;

// A `strings` key with more distinct values than this (in the sampled rows)
// reads as an id / free-text column rather than an enum — omit it and let the
// agent sample on demand.
const ENUM_KEY_MAX_CARDINALITY = 50;
// Show at most this many of the most-frequent values per enum-like key; a key
// with more distinct values renders as truncated.
const VALUE_DISPLAY_LIMIT = 20;

type MetricsQueryResponse = {
  results: Record<string, { rows: ReadonlyArray<ReadonlyArray<unknown>> }>;
};

export type PreflightBlocks = {
  workspaceSchemaBlock: string;
  flowVersionsBlock: string;
};

const sanitizeFlowKey = (flowId: string): string =>
  `f${flowId.replace(/[^a-zA-Z0-9]/g, "")}`;

const formatKeyList = (rows: ReadonlyArray<ReadonlyArray<unknown>>): string =>
  rows.length === 0
    ? "_(none)_"
    : rows.map((r) => `\`${String(r[0])}\` (n=${String(r[1])})`).join(", ");

const escapeCell = (value: string): string => value.replace(/\|/g, "\\|");

// Group (key, value, count, distinct_n) rows into one markdown bullet per key,
// flagging any key whose value list was truncated to the top N. Empty string →
// the schema block omits the whole sampled-values section.
const formatValueLists = (
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
): string => {
  const byKey = rows.reduce<
    Map<string, { values: string[]; distinctN: number }>
  >((acc, r) => {
    const key = String(r[0]);
    const entry = acc.get(key) ?? { values: [], distinctN: Number(r[3]) };
    entry.values.push(`\`${String(r[1])}\` (n=${String(r[2])})`);
    acc.set(key, entry);
    return acc;
  }, new Map());

  return Array.from(byKey.entries())
    .map(([key, { values, distinctN }]) => {
      const suffix =
        distinctN > values.length
          ? ` _(top ${values.length} of ${distinctN} — sample first if you need others)_`
          : "";
      return `- \`${key}\`: ${values.join(", ")}${suffix}`;
    })
    .join("\n");
};

const formatSchemaBlock = (
  volumeRows: ReadonlyArray<ReadonlyArray<unknown>>,
  stringRows: ReadonlyArray<ReadonlyArray<unknown>>,
  numericRows: ReadonlyArray<ReadonlyArray<unknown>>,
  stringValueRows: ReadonlyArray<ReadonlyArray<unknown>>,
): string => {
  const volume = volumeRows[0]?.[0];
  if (volume == null || Number(volume) === 0) {
    return "_(no decisions for this flow in the last 7 days — agent will discover keys on demand)_";
  }

  const stringValues = formatValueLists(stringValueRows);
  const valuesSection = stringValues
    ? [
        "",
        "**Sampled `strings` values — the most-frequent values from the 5000 most-recent decisions (exact, case-sensitive).** Filter and `CASE WHEN` on these literals **exactly as written**. This list is **not guaranteed complete**: a key tagged _(top N of M …)_ has more distinct values than shown, and rarer values can fall outside the sample. Before filtering on a value that isn't listed — or grouping a key tagged truncated — sample it first (`SELECT strings['key'] AS v, COUNT(*) … GROUP BY 1`); never conclude a value doesn't exist just because it's absent here. Keys missing from this section entirely are high-cardinality (ids, free text) — sample them on demand.",
        "",
        stringValues,
      ]
    : [];

  return [
    "**Known output keys (sampled at session start).** Top 40 `strings` and `numerics` keys for this flow, sampled from the 5000 most recent decisions. Reference these directly — no need to re-run discovery unless you hit a key you don't see here.",
    "",
    `- \`strings\` keys: ${formatKeyList(stringRows)}`,
    `- \`numerics\` keys: ${formatKeyList(numericRows)}`,
    "",
    "Prefer `numerics` for aggregation (`AVG`, `quantile_cont`, etc.) and `strings` for categorical / label lookups. Many keys are dual-mapped — `strings[k]` holds the first 40 chars of the raw value, `numerics[k]` holds the numeric cast when castable.",
    ...valuesSection,
  ].join("\n");
};

// `flow_data` only stores `flow_version_id` (a UUID); the human-readable
// version name lives in the control plane. Cross-reference the version IDs
// used in recent decisions with the name map so the agent can resolve a
// version the user names in prose.
const formatFlowVersionsBlock = (
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
  versionIdToName: Record<string, string>,
): string => {
  if (rows.length === 0) {
    return "_(no flow versions seen in the last 30 days)_";
  }

  const tableRows = rows.map((r) => {
    const id = String(r[0]);
    const name = versionIdToName[id];
    const label = name ? escapeCell(name) : "_(unnamed)_";
    return `| \`${escapeCell(id)}\` | ${label} | ${String(r[1])} |`;
  });

  return [
    "**Flow versions used in the last 30 days** (most-used first). `flow_version_id` is a UUID; this table maps it to the human-readable version name.",
    "",
    "| flow_version_id | name | decisions (30d) |",
    "|---|---|---|",
    ...tableRows,
    "",
    "This is a lookup for the user's prose: when they refer to a version by name, resolve it to its `flow_version_id` here and filter on the UUID (`WHERE flow_version_id = '…'`). Keep `flow_version_id` as the grouping/series column in charts — do **not** translate it to the name in SQL; the chart renders these UUIDs as their version names automatically. A version absent from this list is older than 30 days.",
  ].join("\n");
};

export const fetchPreflightBlocks = async (
  baseUrl: string,
  flowId: string,
  versionIdToName: Record<string, string>,
): Promise<PreflightBlocks> => {
  // Hard-reject anything that isn't a UUID before string-interpolating into
  // SQL. The metrics endpoint is auth-gated, but breaking the query on a
  // weird flowId would still produce a confusing prompt rather than a clean
  // fallback.
  if (!FLOW_ID_PATTERN.test(flowId)) {
    throw new Error(`Invalid flowId for preflight snapshot: ${flowId}`);
  }

  const endpoint = `https://${baseUrl}${METRICS_QUERY_PATH}`;
  const safe = sanitizeFlowKey(flowId);

  // Volume check + MAP-key samples + recent flow versions in one batched request.
  const queries = {
    volume: `SELECT COUNT(*) AS n FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '7 days'`,
    [`s_${safe}`]: `SELECT key, COUNT(*) AS n FROM (SELECT strings FROM flow_data WHERE flow_id = '${flowId}' ORDER BY start_datetime DESC LIMIT 5000) sample, UNNEST(map_keys(sample.strings)) AS t(key) GROUP BY 1 ORDER BY 2 DESC LIMIT 40`,
    [`n_${safe}`]: `SELECT key, COUNT(*) AS n FROM (SELECT numerics FROM flow_data WHERE flow_id = '${flowId}' ORDER BY start_datetime DESC LIMIT 5000) sample, UNNEST(map_keys(sample.numerics)) AS t(key) GROUP BY 1 ORDER BY 2 DESC LIMIT 40`,
    // Top values per enum-like `strings` key, with each key's distinct count so
    // the renderer can flag a truncated (top-N) list.
    [`v_${safe}`]: `WITH pairs AS (SELECT entry.key AS k, entry.value AS v, COUNT(*) AS n FROM (SELECT strings FROM flow_data WHERE flow_id = '${flowId}' ORDER BY start_datetime DESC LIMIT 5000) sample, UNNEST(map_entries(sample.strings)) AS t(entry) GROUP BY 1, 2), card AS (SELECT k, COUNT(*) AS distinct_n, SUM(n) AS total_n FROM pairs GROUP BY k), enum_keys AS (SELECT k, distinct_n FROM card WHERE distinct_n <= ${ENUM_KEY_MAX_CARDINALITY} ORDER BY total_n DESC LIMIT 40), ranked AS (SELECT p.k, p.v, p.n, e.distinct_n, ROW_NUMBER() OVER (PARTITION BY p.k ORDER BY p.n DESC) AS rn FROM pairs p JOIN enum_keys e USING (k)) SELECT k, v, n, distinct_n FROM ranked WHERE rn <= ${VALUE_DISPLAY_LIMIT} ORDER BY k, n DESC`,
    versions: `SELECT flow_version_id, COUNT(*) AS n FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '30 days' AND flow_version_id IS NOT NULL GROUP BY 1 ORDER BY 2 DESC LIMIT 40`,
  };

  const { data } = await axios.post<MetricsQueryResponse>(endpoint, {
    queries,
  });

  return {
    workspaceSchemaBlock: formatSchemaBlock(
      data.results?.volume?.rows ?? [],
      data.results?.[`s_${safe}`]?.rows ?? [],
      data.results?.[`n_${safe}`]?.rows ?? [],
      data.results?.[`v_${safe}`]?.rows ?? [],
    ),
    flowVersionsBlock: formatFlowVersionsBlock(
      data.results?.versions?.rows ?? [],
      versionIdToName,
    ),
  };
};
